import {
    FormControl,
    FormDescription,
    FormItem,
    FormLabel,
    FormMessage,
    FormField,
} from "@/components/ui/form";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Tag, Undo2 } from "lucide-react";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useWatch } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { applyPricePresentation } from "@/lib/utils";
import { PricePresentation } from "@/types/Products";

type RoundingOption = {
    value: string;
    label: string;
    description: string;
    roundFunction: (price: number) => number;
    rangeThreshold?: number;
};

// Hi -

export function PriceRoundSelect({
    form,
    selectedMarket,
    optimizationSettings,
    setOptimizationSettings,
    recalculateFormValues,
}: {
    form: any;
    selectedMarket: any;
    optimizationSettings: any;
    setOptimizationSettings: any;
    recalculateFormValues: any;
}) {
    const { currencySymbol } = useCurrency();
    const minPrice =
        useWatch({ control: form.control, name: "minPrice" }) || 20;
    const maxPrice =
        useWatch({ control: form.control, name: "maxPrice" }) || 55;
    const midpoint = (minPrice + maxPrice) / 2;
    const priceRange = maxPrice - minPrice;

    const roundingOptions: RoundingOption[] = [
        {
            value: "none",
            label: "No rounding",
            description: "Keep original price",
            roundFunction: (price) => price,
        },
        {
            value: "charm_99",
            label: "Charm pricing",
            description: "Round to .99",
            roundFunction: (price) => applyPricePresentation(price, "charm_99"),
        },
        {
            value: "charm_95",
            label: "Charm pricing",
            description: "Round to .95",
            roundFunction: (price) => applyPricePresentation(price, "charm_95"),
        },
        {
            value: "charm_49_99",
            label: "Charm pricing",
            description: "Round to .49 or .99",
            roundFunction: (price) =>
                applyPricePresentation(price, "charm_49_99"),
        },
        {
            value: "subunit_5",
            label: "Rounding",
            description: "Round to nearest .05",
            roundFunction: (price) =>
                applyPricePresentation(price, "subunit_5"),
        },
        {
            value: "subunit_10",
            label: "Rounding",
            description: "Round to nearest .10",
            roundFunction: (price) =>
                applyPricePresentation(price, "subunit_10"),
        },
        {
            value: "subunit_25",
            label: "Rounding",
            description: "Round to nearest .25",
            roundFunction: (price) =>
                applyPricePresentation(price, "subunit_25"),
        },
        {
            value: "subunit_50",
            label: "Rounding",
            description: "Round to nearest .50",
            roundFunction: (price) =>
                applyPricePresentation(price, "subunit_50"),
        },
        {
            value: "whole",
            label: "Rounding",
            description: "Round to nearest whole number",
            roundFunction: (price) => applyPricePresentation(price, "whole"),
        },
        {
            value: "whole_5",
            label: "Rounding",
            description: "Round to nearest 5",
            roundFunction: (price) => applyPricePresentation(price, "whole_5"),
            rangeThreshold: 50,
        },
        {
            value: "whole_10",
            label: "Rounding",
            description: "Round to nearest 10",
            roundFunction: (price) => applyPricePresentation(price, "whole_10"),
            rangeThreshold: 100,
        },
        {
            value: "whole_25",
            label: "Rounding",
            description: "Round to nearest 25",
            roundFunction: (price) => applyPricePresentation(price, "whole_25"),
            rangeThreshold: 250,
        },
        {
            value: "whole_50",
            label: "Rounding",
            description: "Round to nearest 50",
            roundFunction: (price) => applyPricePresentation(price, "whole_50"),
            rangeThreshold: 500,
        },
        {
            value: "whole_100",
            label: "Rounding",
            description: "Round to nearest 100",
            roundFunction: (price) =>
                applyPricePresentation(price, "whole_100"),
            rangeThreshold: 1000,
        },
    ];

    const formatPrice = (price: number) => {
        return `${currencySymbol}${price.toFixed(2)}`;
    };

    const getExamplePrices = (roundFunction: (price: number) => number) => {
        const examples = [minPrice, midpoint, maxPrice];
        return examples.map((price) => formatPrice(roundFunction(price)));
    };

    const isOverridden =
        selectedMarket !== "all" &&
        optimizationSettings?.markets?.[selectedMarket]?.presentation !==
            undefined;

    const updatePresentation = (presentation: PricePresentation) => {
        form.setValue("presentation", presentation);

        if (selectedMarket === "all") {
            setOptimizationSettings({
                ...optimizationSettings,
                base_market: {
                    ...optimizationSettings.base_market,
                    presentation,
                },
            });
        } else {
            setOptimizationSettings({
                ...optimizationSettings,
                markets: {
                    ...optimizationSettings.markets,
                    [selectedMarket]: {
                        ...optimizationSettings.markets[selectedMarket],
                        presentation,
                    },
                },
            });
        }
    };

    const handleBaseReset = () => {
        setOptimizationSettings({
            ...optimizationSettings,
            markets: {
                ...optimizationSettings.markets,
                [selectedMarket]: {
                    ...optimizationSettings.markets[selectedMarket],
                    presentation: undefined,
                },
            },
        });
        recalculateFormValues();
    };

    return (
        <FormField
            control={form.control}
            name="presentation"
            render={({ field }) => (
                <FormItem>
                    <FormLabel className="flex items-center gap-2">
                        Price presentation
                        <Button
                            variant="ghost"
                            size="sm"
                            className="ml-auto"
                            onClick={(e) => {
                                e.preventDefault();
                                handleBaseReset();
                            }}
                            disabled={!isOverridden}
                        >
                            {selectedMarket === "all" ? (
                                ""
                            ) : (
                                <>
                                    <Undo2 className="h-3.5 w-3.5" />
                                    Reset
                                </>
                            )}
                        </Button>
                    </FormLabel>
                    <FormDescription>
                        How should the final price be shown to customers?
                    </FormDescription>
                    <Select
                        onValueChange={(value) => {
                            field.onChange(value);
                            updatePresentation(value as PricePresentation);
                        }}
                        value={field.value}
                        defaultValue={field.value}
                    >
                        <FormControl>
                            <SelectTrigger>
                                <div className="flex items-center gap-2">
                                    <Tag className="h-4 w-4 text-muted-foreground" />
                                    <SelectValue />
                                </div>
                            </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                            {roundingOptions.map((option) => (
                                <SelectItem
                                    key={option.value}
                                    value={option.value}
                                    className="flex flex-col items-start py-2"
                                    disabled={
                                        option.rangeThreshold &&
                                        priceRange < option.rangeThreshold
                                    }
                                >
                                    <span className="font-medium">
                                        {option.description}
                                    </span>
                                    {/* <span className="text-xs text-muted-foreground">
                                        {" • "}
                                        {option.description}
                                    </span> */}
                                    <span className="ml-1 text-xs text-muted-foreground">
                                        {"e.g."}
                                        {getExamplePrices(
                                            option.roundFunction
                                        ).map((price, index) => (
                                            <span
                                                key={
                                                    option.value +
                                                    "-" +
                                                    price +
                                                    "-" +
                                                    index
                                                }
                                                className="text-xs text-muted-foreground mt-1 rounded-md bg-muted p-1 py-0.5 ml-1"
                                            >
                                                {price}
                                            </span>
                                        ))}
                                        {"..."}
                                    </span>
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <FormMessage />
                </FormItem>
            )}
        />
    );
}
